import { isAuthApiError } from "@supabase/auth-js";

export type CeremonyOutcome = "cancelled" | "done" | "failed";
export type CodeOutcome = "done" | "failed" | "invalid";

const cancelledCauses = new Set(["AbortError", "NotAllowedError"]);

export const ceremonyOutcome = (error: Error | null): CeremonyOutcome => {
  if (error === null) {
    return "done";
  }
  const isCancelled =
    (error.cause instanceof DOMException && cancelledCauses.has(error.cause.name)) ||
    ("code" in error && error.code === "ERROR_CEREMONY_ABORTED");
  return isCancelled ? "cancelled" : "failed";
};

export const codeOutcome = (error: Error | null): CodeOutcome => {
  if (error === null) {
    return "done";
  }
  return isAuthApiError(error) && error.code === "mfa_verification_failed" ? "invalid" : "failed";
};

export interface CodeFactors {
  readonly challenge: (parameters: {
    readonly factorId: string;
  }) => Promise<{ readonly data: { readonly id: string } | null; readonly error: Error | null }>;
  readonly verify: (parameters: {
    readonly factorId: string;
    readonly challengeId: string;
    readonly code: string;
  }) => Promise<{ readonly error: Error | null }>;
}

const isServerError = (error: Error): boolean =>
  "status" in error && typeof error.status === "number" && error.status >= 500;

// WHY: GoTrue keeps mfa_factors.last_challenged_at unique across all factors, so two challenges in the same microsecond fail with a 500. A challenge grants nothing, so one retry is safe; the code itself is verified once.
const challenge = async (mfa: CodeFactors, factorId: string) => {
  const first = await mfa.challenge({ factorId });
  return first.error !== null && isServerError(first.error) ? mfa.challenge({ factorId }) : first;
};

export const verifyTotp = async (mfa: CodeFactors, factorId: string, code: string): Promise<Error | null> => {
  const challenged = await challenge(mfa, factorId);
  if (challenged.data === null) {
    return challenged.error ?? new Error("GoTrue returned no challenge");
  }
  const { error } = await mfa.verify({ factorId, challengeId: challenged.data.id, code });
  return error;
};
